// Conversión entre lo que escribe la pantalla (texto de formulario) y lo que guarda la base.
// Dinero: la pantalla usa pesos, la base centavos. Fechas: la pantalla usa AAAA-MM-DD.
import type { Campo, Recurso } from "./recursos";

export type Fila = Record<string, unknown> & { id: string };

export function aBase(r: Recurso, entrada: Record<string, unknown>, parcial: boolean) {
  const datos: Record<string, unknown> = {};
  const errores: string[] = [];
  for (const c of r.campos) {
    if (c.soloLectura || (parcial && c.soloAlCrear)) continue;
    if (c.tipo === "json") { if (c.nombre in entrada && typeof entrada[c.nombre] === "object") datos[c.nombre] = entrada[c.nombre]; continue; }
    if (!(c.nombre in entrada)) {
      if (!parcial && c.requerido) errores.push(`Falta ${c.etiqueta}`);
      continue;
    }
    const v = entrada[c.nombre];
    const vacio = v === "" || v === null || v === undefined;
    if (vacio) {
      if (c.requerido) errores.push(`Falta ${c.etiqueta}`);
      else if (c.tipo === "lista") datos[c.nombre] = [];
      else if (c.tipo === "si_no") datos[c.nombre] = false;
      else if (c.porDefecto !== undefined) { if (parcial) datos[c.nombre] = c.porDefecto === "ahora" ? new Date() : c.porDefecto; }
      else if (parcial) datos[c.nombre] = null; // al crear, lo vacío no se manda y la base pone su valor
      continue;
    }
    const valor = convertir(c, v);
    if (valor === undefined) errores.push(`${c.etiqueta} no es válido`);
    else datos[c.nombre] = valor;
  }
  return { datos, errores };
}

function convertir(c: Campo, v: unknown): unknown {
  const s = String(v).trim();
  switch (c.tipo) {
    case "decimal": { const n = Number(s); return s.trim() !== "" && Number.isFinite(n) ? n : undefined; }
    case "entero": { const n = Number(s); return Number.isInteger(n) ? n : undefined; }
    case "dinero": { const n = Number(s.replace(/[$,\s]/g, "")); return Number.isFinite(n) ? Math.round(n * 100) : undefined; }
    case "si_no": return v === true || s === "true" || s === "on" || s === "1";
    case "fecha": return /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00.000Z`) : undefined;
    case "fecha_hora": { const d = new Date(s); return Number.isNaN(d.getTime()) ? undefined : d; }
    case "hora": return /^\d{2}:\d{2}$/.test(s) ? s : undefined;
    case "lista": return Array.isArray(v) ? v.map(String) : s.split(",").map((x) => x.trim()).filter(Boolean);
    case "color": return /^#[0-9a-fA-F]{6}$/.test(s) ? s.toLowerCase() : undefined;
    case "opcion": return c.opciones && !c.opciones.includes(s) ? undefined : s;
    // Imagen: solo una foto subida (WebP, JPG, PNG o AVIF) o una ruta del propio sitio; nada de ligas de fuera ni «javascript:».
    case "imagen": return s === "" || /^data:image\/(webp|jpeg|png|avif);base64,[A-Za-z0-9+/=]+$/.test(s) || /^\/(?!\/)[\w\-./]+$/.test(s) ? s : undefined;
    default: return s;
  }
}

/** Para mostrar o llenar un formulario. */
export function aPantalla(c: Campo, v: unknown): string {
  if (v === null || v === undefined) return "";
  switch (c.tipo) {
    case "dinero": return (Number(v) / 100).toFixed(2).replace(/\.00$/, "");
    case "fecha": return String(v).slice(0, 10);
    case "fecha_hora": return String(v).slice(0, 16);
    case "lista": return Array.isArray(v) ? v.join(", ") : String(v);
    case "si_no": return v ? "true" : "";
    case "json": return JSON.stringify(v);
    default: return String(v);
  }
}

/** Fechas a texto ISO para mandar por la red o guardar en el navegador. */
export function serializar<T>(x: T): T {
  return JSON.parse(JSON.stringify(x, (_k, v) => (typeof v === "bigint" ? Number(v) : v)));
}
